import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import useProviders from "../../../hooks/useProviders";
import { handleLoadSelectOptions, mapValues } from "../../../util/Utilities";
import useAxios from "../../../hooks/useAxios";
import { useFeedBack } from "../../../context/FeedBackContext";
import { FaUpload, FaFileAlt } from "react-icons/fa";

const ProductObservationsCreate = () => {

    const navigate = useNavigate();
    const { setCustomAlert, setLoading } = useFeedBack();

    const [{ providers, loading: providersLoading }, getProviders] = useProviders({ params: { perPage: 500 } }, { useCache: false });
    const [{ data: storeData, loading: loadingStore }, storeRecord] = useAxios({ url: `/product-observations`, method: 'POST' }, { manual: true, useCache: false });

    const [data, setData] = useState({
        guide_number: '',
        purchase_order_number: '',
        provider: null,
        observation: ''
    });

    const [providerProducts, setProviderProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedProductToAdd, setSelectedProductToAdd] = useState(null);
    const [files, setFiles] = useState([]);

    const [{ loading: axiosLoadingProducts }, getProductsByProvider] = useAxios({ method: 'GET' }, { manual: true, useCache: false });

    // When provider changes, fetch products for that provider, ignoring service scope
    useEffect(() => {
        if (data.provider?.value) {
            setLoadingProducts(true);
            setSelectedProducts([]);
            setSelectedProductToAdd(null);
            getProductsByProvider({
                url: `/products`,
                params: {
                    providerId: data.provider.value,
                    ignoreServiceScope: 'true',
                    perPage: 500
                }
            })
                .then((res) => {
                    setProviderProducts(res?.data?.data || []);
                })
                .finally(() => {
                    setLoadingProducts(false);
                });
        } else {
            setProviderProducts([]);
            setSelectedProducts([]);
            setSelectedProductToAdd(null);
        }
    }, [data.provider]);

    useEffect(() => {
        if (storeData) {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: `La observación de productos fue creada exitosamente.`,
                show: true
            });
            navigate('/observaciones');
        }
    }, [storeData]);

    const handleChange = (e) => {
        setData((oldData) => ({
            ...oldData,
            [e.target.name]: e.target.value
        }));
    };

    const handleAddProduct = (prodOption) => {
        if (!prodOption) return;
        const productObj = providerProducts.find((p) => Number(p.id) === Number(prodOption.value));
        if (productObj) {
            if (!selectedProducts.some((p) => Number(p.id) === Number(productObj.id))) {
                setSelectedProducts((prev) => [...prev, { ...productObj, quantity: 1 }]);
            }
        }
        setSelectedProductToAdd(null);
    };

    const handleAddAllProducts = () => {
        setSelectedProducts(providerProducts.map((p) => ({ ...p, quantity: 1 })));
    };

    const handleRemoveProduct = (productId) => {
        setSelectedProducts((prev) => prev.filter((p) => Number(p.id) !== Number(productId)));
    };

    const handleRemoveAllProducts = () => {
        setSelectedProducts([]);
    };

    const handleQuantityChange = (productId, val) => {
        setSelectedProducts((prev) =>
            prev.map((p) =>
                Number(p.id) === Number(productId)
                    ? { ...p, quantity: val }
                    : p
            )
        );
    };

    const handleQuantityBlur = (productId, val) => {
        const num = Math.max(1, parseInt(val, 10) || 1);
        setSelectedProducts((prev) =>
            prev.map((p) =>
                Number(p.id) === Number(productId)
                    ? { ...p, quantity: num }
                    : p
            )
        );
    };

    const handleFilesChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            setFiles((prev) => [...prev, ...newFiles]);
            e.target.value = null;
        }
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!data.guide_number) {
            setCustomAlert({ title: 'Atención', severity: 'warning', message: 'Por favor ingrese el número de guía / factura.', show: true });
            return;
        }

        if (!data.purchase_order_number) {
            setCustomAlert({ title: 'Atención', severity: 'warning', message: 'Por favor ingrese el número de orden de compra.', show: true });
            return;
        }

        if (!data.provider?.value) {
            setCustomAlert({ title: 'Atención', severity: 'warning', message: 'Por favor seleccione un proveedor.', show: true });
            return;
        }

        if (selectedProducts.length === 0) {
            setCustomAlert({ title: 'Atención', severity: 'warning', message: 'Debe agregar al menos un producto a la lista.', show: true });
            return;
        }

        if (!data.observation) {
            setCustomAlert({ title: 'Atención', severity: 'warning', message: 'Por favor ingrese la observación global.', show: true });
            return;
        }

        const formData = new FormData();
        formData.append('guide_number', data.guide_number);
        formData.append('purchase_order_number', data.purchase_order_number);
        formData.append('provider_id', data.provider.value);
        formData.append('observation', data.observation);

        selectedProducts.forEach((p, idx) => {
            formData.append(`products[${idx}][id]`, p.id);
            formData.append(`products[${idx}][quantity]`, Math.max(1, parseInt(p.quantity, 10) || 1));
            formData.append(`product_ids[]`, p.id);
        });

        files.forEach((file) => {
            formData.append('files[]', file);
        });

        storeRecord({
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    };

    const mappedProviderProducts = providerProducts
        .filter((p) => !selectedProducts.some((sp) => Number(sp.id) === Number(p.id)))
        .map((p) => ({
            label: `[${p.code || '--'}] ${p.name} ${p.reference ? `- Ref: ${p.reference}` : ''}`,
            value: p.id
        }));

    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex align-items-center w-100">
                    <div>
                        <h5 className="card-title">
                            Crear Observación de Productos
                        </h5>
                    </div>
                    <div style={{ marginLeft: 'auto' }} className="text-end">
                        <button className="btn btn-xs btn-primary" onClick={() => navigate(-1)}>
                            Volver
                        </button>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-4 mb-3">
                            <div className="form-group">
                                <label className="text-primary fw-bold">
                                    Número de Guía / Factura <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="guide_number"
                                    value={data.guide_number}
                                    onChange={handleChange}
                                    placeholder="Ingrese el número de guía o factura..."
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-4 mb-3">
                            <div className="form-group">
                                <label className="text-primary fw-bold">
                                    Número de Orden de Compra <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="purchase_order_number"
                                    value={data.purchase_order_number}
                                    onChange={handleChange}
                                    placeholder="Ingrese el número de orden de compra..."
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-4 mb-3">
                            <div className="form-group">
                                <label className="text-primary fw-bold">
                                    Proveedor <span className="text-danger">*</span>
                                </label>
                                <AsyncSelect
                                    isClearable
                                    onFocus={() => { getProviders(); }}
                                    value={data.provider}
                                    isLoading={providersLoading}
                                    defaultOptions={mapValues(providers)}
                                    name="provider"
                                    loadOptions={(e) => handleLoadSelectOptions(e, getProviders)}
                                    placeholder="Escriba el nombre del proveedor para buscar..."
                                    onChange={(e) => { setData((prev) => ({ ...prev, provider: e })); }}
                                />
                            </div>
                        </div>

                        {/* Productos del Proveedor */}
                        <div className="col-md-12 my-3">
                            <div className="card p-3 border">
                                <h5 className="text-primary mb-3">
                                    Productos del Proveedor Seleccionado
                                </h5>

                                {!data.provider ? (
                                    <div className="alert alert-info mb-0">
                                        Por favor seleccione un proveedor primero para ver sus productos cargados.
                                    </div>
                                ) : loadingProducts ? (
                                    <div className="text-center p-3">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Cargando productos...</span>
                                        </div>
                                        <p className="mt-2 mb-0">Cargando productos del proveedor...</p>
                                    </div>
                                ) : providerProducts.length === 0 ? (
                                    <div className="alert alert-warning mb-0">
                                        No se encontraron productos cargados para este proveedor.
                                    </div>
                                ) : (
                                    <div>
                                        <div className="row align-items-center mb-3">
                                            <div className="col-md-8">
                                                <label className="text-primary small fw-bold">
                                                    Agregar Producto a la Lista de Observación
                                                </label>
                                                <Select
                                                    key={data.provider?.value}
                                                    value={selectedProductToAdd}
                                                    options={mappedProviderProducts}
                                                    placeholder="Seleccione un producto para agregar..."
                                                    onChange={handleAddProduct}
                                                    isClearable
                                                    isSearchable
                                                />
                                            </div>
                                            <div className="col-md-4 text-end mt-3 mt-md-0">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm me-2"
                                                    onClick={handleAddAllProducts}
                                                    disabled={mappedProviderProducts.length === 0}
                                                >
                                                    Agregar Todos ({providerProducts.length})
                                                </button>
                                                {selectedProducts.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={handleRemoveAllProducts}
                                                    >
                                                        Quitar Todos
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Tabla de Productos Seleccionados */}
                                        {selectedProducts.length > 0 ? (
                                            <div className="table-responsive mt-3">
                                                <table className="table table-bordered align-middle">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th style={{ width: 50 }}>#</th>
                                                            <th>Código</th>
                                                            <th>Nombre del Producto</th>
                                                            <th>Referencia</th>
                                                            <th>Categoría</th>
                                                            <th style={{ width: 120 }} className="text-center">Cantidad</th>
                                                            <th style={{ width: 80 }} className="text-center">Acción</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedProducts.map((prod, idx) => (
                                                            <tr key={prod.id}>
                                                                <td><b>{idx + 1}</b></td>
                                                                <td><code>{prod.code || '--'}</code></td>
                                                                <td>{prod.name}</td>
                                                                <td>{prod.reference || '--'}</td>
                                                                <td>{prod.category?.name || '--'}</td>
                                                                <td className="text-center">
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        step="1"
                                                                        className="form-control form-control-sm text-center mx-auto"
                                                                        style={{ maxWidth: '90px' }}
                                                                        value={prod.quantity !== undefined ? prod.quantity : 1}
                                                                        onChange={(e) => handleQuantityChange(prod.id, e.target.value)}
                                                                        onBlur={(e) => handleQuantityBlur(prod.id, e.target.value)}
                                                                    />
                                                                </td>
                                                                <td className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-xs btn-danger"
                                                                        title="Quitar producto"
                                                                        onClick={() => handleRemoveProduct(prod.id)}
                                                                    >
                                                                        X
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="alert alert-secondary mb-0 text-center">
                                                No ha seleccionado ningún producto aún. Seleccione productos arriba para aplicarles la observación global.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Observación Global */}
                        <div className="col-md-12 mb-3">
                            <div className="form-group">
                                <label className="text-primary fw-bold">
                                    Observación Global <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    name="observation"
                                    value={data.observation}
                                    onChange={handleChange}
                                    placeholder="Ingrese la observación detallada sobre los productos seleccionados..."
                                    rows={4}
                                    required
                                />
                            </div>
                        </div>

                        {/* Archivos de Prueba / Evidencia */}
                        <div className="col-md-12 mb-3">
                            <div className="form-group">
                                <label className="text-primary fw-bold d-block mb-1">
                                    Archivos de Prueba / Evidencia (Opcional)
                                </label>
                                <div className="border border-2 border-dashed rounded p-3 text-center bg-light">
                                    <input
                                        type="file"
                                        multiple
                                        id="evidence-files-input"
                                        className="d-none"
                                        onChange={handleFilesChange}
                                    />
                                    <label htmlFor="evidence-files-input" className="btn btn-outline-primary mb-2" style={{ cursor: 'pointer' }}>
                                        <FaUpload className="me-2" /> Seleccionar Archivos
                                    </label>
                                    <p className="text-muted small mb-0">
                                        Puede adjuntar imágenes (JPG, PNG), documentos (PDF) o archivos de respaldo que sirvan de prueba.
                                    </p>
                                </div>

                                {files.length > 0 && (
                                    <div className="mt-3">
                                        <label className="small fw-bold text-secondary mb-2">
                                            Archivos seleccionados ({files.length}):
                                        </label>
                                        <div className="list-group">
                                            {files.map((file, idx) => (
                                                <div key={idx} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                                    <div className="d-flex align-items-center text-truncate">
                                                        <FaFileAlt className="text-primary me-2 flex-shrink-0" />
                                                        <span className="text-truncate" title={file.name}>
                                                            {file.name}
                                                        </span>
                                                        <span className="badge bg-secondary ms-2 flex-shrink-0">
                                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-xs btn-outline-danger ms-2 flex-shrink-0"
                                                        onClick={() => handleRemoveFile(idx)}
                                                        title="Quitar archivo"
                                                    >
                                                        X
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="text-end mt-4">
                        <button type="button" className="btn btn-danger me-2" onClick={() => navigate(-1)}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loadingStore}>
                            {loadingStore ? 'Guardando...' : 'Crear Observación'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductObservationsCreate;
